import { createContext, useContext, useState, useEffect } from 'react'
import { PropTypes } from 'prop-types'
import axios from 'axios'
import { useUser } from '../../Permissions'
import api from './organization.api'

import { STATUS } from '../Utils'

// Create the Context
const OrganizationContext = createContext()

const OrganizationProvider = ({ children }) => {
  const [groupData, setGroupData] = useState([])
  const [groupStatus, setGroupStatus] = useState(STATUS.IDLE)
  const [groupError, setGroupError] = useState(null)

  const [teamData, setTeamData] = useState([])
  const [teamStatus, setTeamStatus] = useState(STATUS.IDLE)
  const [teamError, setTeamError] = useState(null)

  const [agentData, setAgentData] = useState([])
  const [agentStatus, setAgentStatus] = useState(STATUS.IDLE)
  const [agentError, setAgentError] = useState(null)

  const user = useUser()


  // generic fetch function that can fetch groups, teams, or agents
  const fetchOrg = async (status, setData, setStatus, setError, apiGet, cancelToken, queryParams = '') => {
    if (status !== STATUS.IDLE) {
      return
    }

    setStatus(STATUS.PENDING)
    try {
      const org = await apiGet(cancelToken, queryParams)
      setData(org)
      setStatus(STATUS.RESOLVED)
    } catch (error) {
      if (!axios.isCancel(error)) {
        setError(error)
        setStatus(STATUS.REJECTED)
      }
    }
  }

  useEffect(() => {
    const CancelToken = axios.CancelToken
    const source = CancelToken.source()

    // Workaround for ADO tickets #116271 & #117191
    const queryParams = user.useQmViewScope ? '?useQmViewScope=true' : ''

    fetchOrg(groupStatus, setGroupData, setGroupStatus, setGroupError, api.getGroups, source.token)
    fetchOrg(teamStatus, setTeamData, setTeamStatus, setTeamError, api.getTeams, source.token)
    fetchOrg(agentStatus, setAgentData, setAgentStatus, setAgentError, api.getAgents, source.token, queryParams)

    return () => {
      source.cancel()
    }
  }, [])

  const context = {
    group: { data: groupData, status: groupStatus, error: groupError },
    groupData,
    groupStatus,
    groupError,
    team: { data: teamData, status: teamStatus, error: teamError },
    teamData,
    teamStatus,
    teamError,
    agent: { data: agentData, status: agentStatus, error: agentError },
    agentData,
    agentStatus,
    agentError,
  }

  return <OrganizationContext.Provider value={context}>{children}</OrganizationContext.Provider>
}

OrganizationProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

const useOrganization = () => {
  const context = useContext(OrganizationContext)
  if (context === undefined) {
    throw new Error('useOrganization must be used within a OrganizationProvider')
  }
  return context
}

export { OrganizationProvider, OrganizationContext, useOrganization }
